import { useEffect, useState, useRef, useCallback } from "react"
import { Box, Typography, FormControlLabel, Checkbox, Stack, InputAdornment, Alert, Tooltip } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMessage, faPrint } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { Formik, Form, FormikHelpers } from "formik"
import * as Yup from "yup"
import { isEmpty } from "lodash-es"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import {
    StandardErrorDialog,
    useStandardErrorDialogState,
} from "../../../../../../shared/components/dialogs/StandardErrorDialog"
import { UserToggle } from "./UserToggle"
import { FormikSubmitButton, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { checkInGuest } from "../../serviceCenterHomeApi"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectServiceCenterId } from "../../../../../login/loginSelectors"
import { CompanySelect } from "../../../../../../shared/components/createOrder/orderOnBehalf/CompanySelect"
import { UserType } from "../../../../../login/loginTypes"
import { UserDetailsProps } from "../../../../../../shared/components/createOrder/orderOnBehalf/OrderOnBehalfFlow"
import { UserSelector } from "../../../../../../shared/components/createOrder/orderOnBehalf/UserSelector"
import { useLabelPrinter } from "../../hooks/useLabelPrinter"
import { GroupType, SingleType } from "../../serviceCenterHomeTypes"
import { ApiError } from "../../../../../../core/api/errorTypes"
import { LabelPrinterDialogVariant, LabelPrinterExtensionDialog } from "../../../../../../shared/components/dialogs/LabelPrinterExtensionDialog"
import { defaultUserDetailsState } from "../../../../../../shared/components/createOrder/defaults"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"

const useStyles = makeStyles()(() => ({
    modal: {
        padding: "3rem",
    },
    guestDetails: {
        width: "65%",
    },
    settingsSection: {
        width: "35%",
    },
    checkinButton: {
        padding: ".5rem 3rem !important",
        minWidth: "10rem",
    },
    printingDialogContent: {
        padding: "0 0 3rem",
        minWidth: "18.75rem",
    },
    printingErrorAlert: {
        marginTop: "1.5rem",
    },
    printingDialogButton: {
        marginTop: "1.5rem",
    },
    checkboxLabel: {
        alignItems: "flex-start",
    },
    checkboxAlignTop: {
        alignSelf: "flex-start",
        marginTop: "-0.5rem",
    },
}))

type FormValues = {
    userType: "single" | "group"
    guestName: string
    guestCompany: string
    numberOfGuests: string
    printGuestCard: boolean
    sendNotification: boolean
    messageToHost: string
}

export function GuestCheckInModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    const { classes } = useStyles()

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="service-center-reports-dashboard-check-in-modal-title">
                    <>Guest check-in</>
                </LocalizedStrict>
            }
            open={open}
            onClose={onClose}
            maxWidthProp="md"
            scroll="body"
        >
            <Box className={classes.modal}>
                <CheckInForm
                    variant="modal"
                    handleFinish={() => {
                        onClose()
                    }}
                />
            </Box>
        </StandardDialog>
    )
}

export function CheckInForm({ handleFinish, variant }: { handleFinish?: () => void; variant?: "modal" | "block" }) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const serviceCenterId = useAppSelector(selectServiceCenterId)
    const [userDetailsState, setUserDetailsState] = useState<UserDetailsProps>(defaultUserDetailsState)
    const [alerts, setAlerts] = useState<
        Array<{
            id: string
            type: "success" | "error"
            message: string
            dismissOnClick?: boolean
        }>
    >([])
    const [printDialogOpen, setPrintDialogOpen] = useState(false)
    const alertTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const currentCheckInTypeRef = useRef<"single" | "group">("single")
    const printDataRef = useRef<{
        userType: "single" | "group"
        guestName: string
        guestCompany: string
        numberOfGuests: string
        hostName: string
    } | null>(null)

    const { errorDialog, openErrorDialog, closeErrorDialog } = useStandardErrorDialogState()

    const {
        printGuestLabel,
        printGroupLabels,
        hasError: hasPrintError,
        hasSuccess: hasPrintSuccess,
        printErrorMessage,
        isInstalledError,
        isPrinterNotFound,
        clearState,
        isLoading: isPrinting,
    } = useLabelPrinter()

    const addAlert = useCallback(
        (
            type: "success" | "error",
            message: string,
            options?: { dismissOnClick?: boolean; autoDismissMs?: number },
        ) => {
            const id = Date.now().toString()
            const { dismissOnClick = false, autoDismissMs } = options ?? {}

            // Clear any existing timeout
            if (alertTimeoutRef.current) {
                clearTimeout(alertTimeoutRef.current)
                alertTimeoutRef.current = null
            }

            setAlerts((prevAlerts) => [...prevAlerts, { id, type, message, dismissOnClick }])

            if (autoDismissMs) {
                alertTimeoutRef.current = setTimeout(() => {
                    setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id))
                    alertTimeoutRef.current = null
                }, autoDismissMs)
            }
        },
        [],
    )

    const removeAlert = useCallback((id: string) => {
        setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id))
    }, [])

    const { mutateAsync } = useApiMutation(checkInGuest, {
        async onSuccess(_params, data) {
            const isGroupCheckIn = currentCheckInTypeRef.current === "group"

            const successMessage = isGroupCheckIn
                ? l10n.getString("service-center-reports-check-group-success-title")
                : typeof data === "object" && "serviceResponse" in data && data?.serviceResponse?.displayMessage
                  ? data?.serviceResponse.displayMessage
                  : l10n.getString("service-center-reports-check-success-title")

            addAlert("success", successMessage, {
                dismissOnClick: true,
                autoDismissMs: 3000,
            })
        },
        async onError(_, error) {
            const errorTitle = l10n.getString("service-center-check-in-error-title")
            let errorDescription = l10n.getString("service-center-check-in-error-generic")

            if (error instanceof ApiError) {
                if (error.hasApiMessage) {
                    errorDescription = error.displayMessage
                } else if (error.status === 404) {
                    errorDescription = l10n.getString("service-center-check-in-error-not-found")
                } else if (error.status === 400) {
                    errorDescription = l10n.getString("service-center-check-in-error-invalid-data")
                }
            } else if (error && typeof error === "object" && "displayMessage" in error) {
                errorDescription = error.displayMessage as string
            }

            openErrorDialog({
                title: errorTitle,
                description: errorDescription,
            })
        },
    })

    useEffect(() => {
        // Close the printing dialog and finish if printing was successful
        if (hasPrintSuccess && printDialogOpen) {
            setPrintDialogOpen(false)
            handleFinish?.()
            clearState()
        }
    }, [hasPrintSuccess, printDialogOpen])

    // Dismiss alerts that have dismissOnClick when user clicks anywhere
    useEffect(() => {
        const hasDismissOnClickAlerts = alerts.some((alert) => alert.dismissOnClick)
        if (!hasDismissOnClickAlerts) {
            return
        }

        const handleClick = () => {
            setAlerts((prevAlerts) => prevAlerts.filter((alert) => !alert.dismissOnClick))
        }

        // Use a small delay so the click that triggered the check-in doesn't immediately dismiss
        const timeoutId = setTimeout(() => {
            document.addEventListener("click", handleClick, { once: true })
        }, 200)

        return () => {
            clearTimeout(timeoutId)
            document.removeEventListener("click", handleClick)
        }
    }, [alerts])

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (alertTimeoutRef.current) {
                clearTimeout(alertTimeoutRef.current)
            }
        }
    }, [])

    const removeAlertByMessage = (message: string) => {
        setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.message !== message))
    }

    const handleClosePrintDialog = () => {
        setPrintDialogOpen(false)
        clearState()
        printDataRef.current = null
        handleFinish?.()
    }

    const handleRetryPrint = async () => {
        const printData = printDataRef.current
        if (!printData) {
            return
        }

        clearState()
        try {
            if (printData.userType === "single") {
                await printGuestLabel({
                    visitorName: printData.guestName,
                    visitorCompany: printData.guestCompany,
                    hostName: printData.hostName,
                })
            } else {
                await printGroupLabels({
                    groupName: printData.guestCompany,
                    groupSize: +printData.numberOfGuests,
                    hostName: printData.hostName,
                })
            }
        } catch (error) {
            console.error("Print retry failed:", error)
        }
    }

    const validationSchema = Yup.object().shape({
        userType: Yup.string()
            .oneOf(["single", "group"], "Invalid user type")
            .required(l10n.getString("required-field")),
        guestName: Yup.string().when("userType", {
            is: "single",
            then: (schema) =>
                schema.required(l10n.getString("required-field")).min(3, l10n.getString("required-field")),
            otherwise: (schema) => schema.notRequired(),
        }),
        // companyId: Yup.number().required(l10n.getString("required-field")),
        // selectedUserId: Yup.number().required(l10n.getString("required-field")),
        numberOfGuests: Yup.number().when("userType", {
            is: "group",
            then: (schema) => schema.required(l10n.getString("required-field")),
            otherwise: (schema) => schema.notRequired(),
        }),
        guestCompany: Yup.string().required(l10n.getString("required-field")),
        printGuestCard: Yup.boolean(),
        sendNotification: Yup.boolean(),
        messageToHost: Yup.string().max(500, "Message must be no more than 500 characters"),
    })

    const initialValues: FormValues = {
        userType: "single",
        guestName: "",
        numberOfGuests: "",
        guestCompany: "",
        printGuestCard: true,
        sendNotification: true,
        messageToHost: "",
    }

    async function handleSubmit(values: FormValues, helpers: FormikHelpers<FormValues>) {
        try {
            let checkIns: SingleType[] | GroupType[] = []

            if (values.userType === "single") {
                checkIns = [
                    {
                        type: "SINGLE",
                        host: {
                            company: {
                                id: userDetailsState?.companyId ? +userDetailsState?.companyId : 0,
                            },
                            user: {
                                id: userDetailsState?.selectedUserId ?? 0,
                            },
                        },
                        guest: {
                            company: values.guestCompany,
                            name: values.guestName,
                        },
                    },
                ]
            }
            if (values.userType === "group") {
                checkIns = [
                    {
                        type: "GROUP",
                        host: {
                            company: {
                                id: userDetailsState?.companyId ? +userDetailsState?.companyId : 0,
                            },
                            user: {
                                id: userDetailsState?.selectedUserId ?? 0,
                            },
                        },
                        group: {
                            company: values.guestCompany,
                            count: +values.numberOfGuests,
                        },
                    },
                ]
            }

            if (!userDetailsState?.selectedUserId || !userDetailsState?.companyId) {
                addAlert("error", l10n.getString("service-center-reports-check-validation-error"))
                return
            }

            // Set the check-in type for the success callback
            currentCheckInTypeRef.current = values.userType

            await mutateAsync({
                serviceCenterId: serviceCenterId ?? 0,
                checkIns,
                notifyHost: values.sendNotification,
                messageToHost: values.messageToHost,
            })
            if (values.printGuestCard) {
                // Store print data for potential retry
                printDataRef.current = {
                    userType: values.userType,
                    guestName: values.guestName,
                    guestCompany: values.guestCompany,
                    numberOfGuests: values.numberOfGuests,
                    hostName: userDetailsState?.selectedUserName ?? "",
                }

                // Clear any previous printer state before starting new print
                clearState()
                setPrintDialogOpen(true)

                try {
                    if (values.userType === "single") {
                        await printGuestLabel({
                            visitorName: values.guestName,
                            visitorCompany: values.guestCompany,
                            hostName: userDetailsState?.selectedUserName ?? "",
                        })
                    }
                    if (values.userType === "group") {
                        await printGroupLabels({
                            groupName: values.guestCompany,
                            groupSize: +values.numberOfGuests,
                            hostName: userDetailsState?.selectedUserName ?? "",
                        })
                    }
                } catch (error) {
                    // Error will be shown in the dialog via the useEffect
                    console.error("Printing failed:", error)
                }
            } else {
                // No printing needed, finish immediately
                handleFinish?.()
            }

            helpers.resetForm()
            setUserDetailsState(defaultUserDetailsState)
        } catch (error) {
            console.error("Check-in failed:", error)
        }
    }

    return (
        <>
            <LabelPrinterExtensionDialog open={isInstalledError} onClose={clearState} variant={LabelPrinterDialogVariant.NOT_INSTALLED} />
            <LabelPrinterExtensionDialog open={isPrinterNotFound} onClose={clearState} variant={LabelPrinterDialogVariant.PRINTER_NOT_FOUND} />
            <StandardErrorDialog dialogDetails={errorDialog} onClose={closeErrorDialog} />
            <Formik<FormValues>
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue }) => (
                    <Form>
                        <StandardDialog
                            open={printDialogOpen}
                            hideCloseIcon={isPrinting && !hasPrintError}
                            onClose={handleClosePrintDialog}
                            dialogTitle={
                                hasPrintError ? (
                                    <LocalizedStrict id="app-error-boundary-title">
                                        <>Something went wrong</>
                                    </LocalizedStrict>
                                ) : (
                                    <LocalizedStrict id="service-center-reports-dashboard-printing-labels-modal-title">
                                        <>Printing guest labels</>
                                    </LocalizedStrict>
                                )
                            }
                        >
                            <Box className={classes.printingDialogContent}>
                                {hasPrintError ? (
                                    <Stack alignItems="center" gap={2}>
                                        <Typography variant="body2" color="text.secondary">
                                            {printErrorMessage}
                                        </Typography>
                                        <Stack direction="row" spacing={2} className={classes.printingDialogButton}>
                                            <StandardButton
                                                variant="outlined"
                                                fullWidth={false}
                                                onClick={handleClosePrintDialog}
                                            >
                                                <LocalizedStrict id="error-dialog-close-button">
                                                    <>Close</>
                                                </LocalizedStrict>
                                            </StandardButton>
                                            <StandardButton
                                                variant="contained"
                                                fullWidth={false}
                                                onClick={handleRetryPrint}
                                            >
                                                <LocalizedStrict id="query-error-boundary-retry-button">
                                                    <>Retry</>
                                                </LocalizedStrict>
                                            </StandardButton>
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Stack alignItems="center" gap={3}>
                                        <FontAwesomeIcon icon={faPrint} size="3x" />
                                        <LocalizedStrict id="service-center-reports-check-in-modal-guest-details-printer-dialog-printing">
                                            <Typography>Printing label...</Typography>
                                        </LocalizedStrict>
                                    </Stack>
                                )}
                            </Box>
                        </StandardDialog>
                        {!isEmpty(alerts) && (
                            <Stack marginBottom="2rem" spacing="1rem">
                                {alerts.map((alert) => (
                                    <Alert
                                        key={alert.id}
                                        onClose={() => removeAlert(alert.id)}
                                        severity={alert.type}
                                        variant="filled"
                                    >
                                        {alert.message}
                                    </Alert>
                                ))}
                            </Stack>
                        )}
                        <Stack flexDirection="row" justifyContent="space-between" spacing="2rem" marginBottom="2rem">
                            <Box className={classes.guestDetails}>
                                {variant === "modal" && (
                                    <LocalizedStrict id="service-center-reports-check-in-modal-guest-details">
                                        <Typography variant="subtitle1" gutterBottom>
                                            Guest details
                                        </Typography>
                                    </LocalizedStrict>
                                )}
                                <Stack spacing="1.5rem" marginBottom="3rem">
                                    <CompanySelect
                                        userDetails={userDetailsState}
                                        labelTranslationKey="create-order-on-behalf-of-dialog-select-company-host-field"
                                        setUserDetails={(data) => {
                                            if (typeof data === "function") {
                                                const newData = data(userDetailsState)
                                                setFieldValue("companyId", newData?.companyId)
                                            }
                                            setUserDetailsState(data)
                                        }}
                                        id={serviceCenterId ?? 0}
                                        userType={UserType.SERVICE_CENTER}
                                    />

                                    <UserSelector
                                        labelTranslationKey="create-order-on-behalf-of-dialog-select-host-field"
                                        userDetails={userDetailsState}
                                        setUserDetails={(value) => {
                                            setUserDetailsState(value)
                                            removeAlertByMessage(
                                                l10n.getString("service-center-reports-check-validation-error"),
                                            )
                                        }}
                                        id={serviceCenterId ?? 0}
                                        userType={UserType.SERVICE_CENTER}
                                    />
                                </Stack>
                                <UserToggle
                                    value={values.userType}
                                    onChange={(value) => setFieldValue("userType", value)}
                                />
                                {values.userType === "single" ? (
                                    <LocalizedStrict
                                        id="service-center-reports-check-in-modal-guest-name"
                                        attrs={{ label: true }}
                                    >
                                        <FormikTextField<FormValues>
                                            name="guestName"
                                            label="Guest name"
                                            fullWidth
                                            margin="normal"
                                        />
                                    </LocalizedStrict>
                                ) : (
                                    <LocalizedStrict
                                        id="service-center-reports-check-in-modal-number-of-guests"
                                        attrs={{ label: true }}
                                    >
                                        <FormikTextField<FormValues>
                                            name="numberOfGuests"
                                            type="number"
                                            label="Number of guests"
                                            fullWidth
                                            margin="normal"
                                        />
                                    </LocalizedStrict>
                                )}
                                <LocalizedStrict
                                    id={
                                        values.userType === "single"
                                            ? "service-center-reports-check-in-modal-guest-company"
                                            : "service-center-reports-check-in-modal-guests-company"
                                    }
                                    attrs={{ label: true }}
                                >
                                    <FormikTextField<FormValues>
                                        name="guestCompany"
                                        label="Guest company"
                                        fullWidth
                                        margin="normal"
                                    />
                                </LocalizedStrict>
                            </Box>
                            <Box className={classes.settingsSection}>
                                {variant === "modal" && (
                                    <LocalizedStrict id="service-center-reports-check-in-modal-settings">
                                        <Typography variant="subtitle1" gutterBottom>
                                            Settings
                                        </Typography>
                                    </LocalizedStrict>
                                )}
                                <LocalizedStrict
                                    id="service-center-reports-check-in-modal-print-guest-card"
                                    attrs={{ label: true }}
                                >
                                    <FormControlLabel
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                className={classes.checkboxAlignTop}
                                                checked={values.printGuestCard}
                                                onChange={(e) => setFieldValue("printGuestCard", e.target.checked)}
                                            />
                                        }
                                        label="Print guest card"
                                    />
                                </LocalizedStrict>
                                <Tooltip
                                    title="Host will receive notification that the guest has been checked in."
                                    arrow
                                >
                                    <Box>
                                        <LocalizedStrict
                                            id="service-center-reports-check-in-modal-send-host-notification"
                                            attrs={{ label: true }}
                                        >
                                            <FormControlLabel
                                                className={classes.checkboxLabel}
                                                control={
                                                    <LocalizedStrict
                                                        attrs={{ title: true }}
                                                        id="service-center-reports-check-in-modal-send-host-notification-tooltip"
                                                    >
                                                        <Checkbox
                                                            className={classes.checkboxAlignTop}
                                                            checked={values.sendNotification}
                                                            onChange={(e) =>
                                                                setFieldValue("sendNotification", e.target.checked)
                                                            }
                                                        />
                                                    </LocalizedStrict>
                                                }
                                                label="Notify host"
                                            />
                                        </LocalizedStrict>
                                    </Box>
                                </Tooltip>
                                {variant === "modal" && (
                                    <Box display="flex" alignItems="center" marginTop={2}>
                                        <LocalizedStrict
                                            id="service-center-reports-check-in-modal-write-message-to-host"
                                            attrs={{ label: true }}
                                        >
                                            <FormikTextField<FormValues>
                                                name="messageToHost"
                                                label="Write a message to host"
                                                fullWidth
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <FontAwesomeIcon icon={faMessage} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </LocalizedStrict>
                                    </Box>
                                )}
                            </Box>
                        </Stack>

                        <Stack justifyContent="center" alignItems="center">
                            <LocalizedStrict id="service-center-reports-check-in-modal-check-in-button">
                                <FormikSubmitButton fullWidth={false} className={classes.checkinButton} type="submit">
                                    Check-in
                                </FormikSubmitButton>
                            </LocalizedStrict>
                        </Stack>
                    </Form>
                )}
            </Formik>
        </>
    )
}
